import React, { useState } from 'react';
import { ApolloProvider, gql, useMutation, useQuery } from '@apollo/client';
import ahoy from 'ahoy.js';
import client from '../lib/apolloClient';
import AppLayout from '../components/Succeedo/Layout/AppLayout';
import FinancingCalculatorPage from '../components/Succeedo/FinancingCalculator/FinancingCalculatorPage';
import ShareSnapshotModal from '../components/Succeedo/DealTeam/ShareSnapshotModal';

const GET_FINANCING_DATA = gql`
  query GetFinancingData {
    currentUser {
      id
      deal_teams {
        id
        name
        status
      }
    }
    lenders {
      id
      name
      description
      roi
      loan_amount_minimum
      loan_amount_maximum
      loan_amortization_minimum
      loan_amortization_maximum
      contact_name
      contact_email
      fees_info
      structure_info
      prepayment_info
      small_logo_url
      rate_bands {
        id
        name
        loan_amount_lower_band
        loan_amount_upper_band
        variable_interest_rate
        maximum_loan_to_value
      }
    }
    valuation_snapshots(limit: 1)
    financing_snapshots(limit: 5)
    introduction_requests
    featured_partners(limit: 3, category: "lender")
  }
`;

const CREATE_INTRO_REQUEST = gql`
  mutation CreateIntroductionRequest($partnerCategory: String!, $coachProfileId: ID) {
    create_introduction_request(partner_category: $partnerCategory, coach_profile_id: $coachProfileId) {
      introduction_request
      errors
    }
  }
`;

const SAVE_FINANCING_SCENARIO = gql`
  mutation SaveFinancingScenario(
    $loan_amount: Float!
    $interest_rate: Float!
    $loan_term_months: Int!
    $monthly_payment: Float!
    $total_interest: Float
    $total_cost: Float
    $lender_name: String
  ) {
    save_financing_scenario(
      loan_amount: $loan_amount
      interest_rate: $interest_rate
      loan_term_months: $loan_term_months
      monthly_payment: $monthly_payment
      total_interest: $total_interest
      total_cost: $total_cost
      lender_name: $lender_name
    ) {
      success
      errors
    }
  }
`;

const SAVE_FINANCING_SNAPSHOT = gql`
  mutation SaveFinancingSnapshot($inputs: JSON!, $results: JSON!, $lenderName: String) {
    save_financing_snapshot(inputs: $inputs, results: $results, lender_name: $lenderName) {
      snapshot
      errors
    }
  }
`;

const SET_PRIMARY_FINANCING_SNAPSHOT = gql`
  mutation SetPrimaryFinancingSnapshot($id: ID!) {
    set_primary_financing_snapshot(id: $id) {
      snapshot
      errors
    }
  }
`;

function FinancingCalculatorInner() {
  const { data, loading, error, refetch } = useQuery(GET_FINANCING_DATA);
  const [createIntroRequest, { loading: introLoading }] = useMutation(CREATE_INTRO_REQUEST);
  const [saveScenario] = useMutation(SAVE_FINANCING_SCENARIO);
  const [saveFinancingSnapshot] = useMutation(SAVE_FINANCING_SNAPSHOT);
  const [setPrimaryFinancingSnapshot] = useMutation(SET_PRIMARY_FINANCING_SNAPSHOT);
  const [currentSnapshotId, setCurrentSnapshotId] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const lenders = data?.lenders || [];
  const introductionRequests = data?.introduction_requests || [];
  const featuredPartners = data?.featured_partners || [];
  const financingSnapshots = data?.financing_snapshots || [];

  const activeDealTeam = data?.currentUser?.deal_teams?.[0] || null;
  const currentSnapshot = financingSnapshots.find((s) => s.id === currentSnapshotId);
  const preselectedSnapshot = currentSnapshot
    ? {
        id: currentSnapshot.id,
        type: 'financing_snapshot',
        label: currentSnapshot.lender_name
          ? `Financing — ${currentSnapshot.lender_name}`
          : `Financing — ${new Date(currentSnapshot.created_at).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}`,
        is_primary: !!currentSnapshot.is_primary,
      }
    : null;

  // Auto-populate purchase price from latest valuation snapshot midpoint
  const snapshots = data?.valuation_snapshots || [];
  const latestSnapshot = snapshots[0];
  const defaultPurchasePrice = latestSnapshot
    ? Math.round(((parseFloat(latestSnapshot.value_range?.low) || 0) + (parseFloat(latestSnapshot.value_range?.high) || 0)) / 2)
    : null;

  const handleRequestIntroduction = async (category) => {
    try {
      const { data: result } = await createIntroRequest({
        variables: { partnerCategory: category },
      });
      const errors = result?.create_introduction_request?.errors || [];
      if (errors.length > 0) {
        console.error('Intro request errors:', errors);
        return;
      }
      if (typeof window.ahoy !== 'undefined') {
        window.ahoy.track('succeedo_introduction_requested', {
          partner_category: category,
          source: 'financing_calculator',
        });
      }
      refetch();
    } catch (e) {
      console.error('Failed to create intro request', e);
    }
  };

  const handleSaveScenario = async (scenarioData) => {
    try {
      const { data: result } = await saveScenario({ variables: scenarioData });
      const errors = result?.save_financing_scenario?.errors || [];
      if (errors.length > 0) {
        console.error('Save scenario errors:', errors);
      }
    } catch (e) {
      console.error('Failed to save financing scenario', e);
    }
  };

  const handleSaveSnapshot = async ({ inputs, results, lenderName }) => {
    try {
      const { data: result } = await saveFinancingSnapshot({
        variables: { inputs, results, lenderName },
      });

      const errors = result?.save_financing_snapshot?.errors || [];
      if (errors.length > 0) {
        console.error('Save financing snapshot errors:', errors);
        return null;
      }

      const snapshot = result?.save_financing_snapshot?.snapshot;
      if (snapshot) {
        setCurrentSnapshotId(snapshot.id);
      }

      refetch();
      ahoy.track('succeedo_financing_saved', { snapshot_id: snapshot?.id });
      return snapshot;
    } catch (e) {
      console.error('Failed to save financing snapshot', e);
      return null;
    }
  };

  const handleLoadSnapshot = (snapshot) => {
    setCurrentSnapshotId(snapshot.id);
  };

  const handleSetPrimary = async (snapshotId) => {
    try {
      await setPrimaryFinancingSnapshot({ variables: { id: String(snapshotId) } });
      refetch();
    } catch (e) {
      console.error('Failed to set primary financing snapshot', e);
    }
  };

  return (
    <AppLayout breadcrumb="FINANCING CALCULATOR">
      {loading && (
        <div className="max-w-4xl mx-auto py-8 px-4">
          <p className="text-muted-foreground text-sm">Loading lenders...</p>
        </div>
      )}
      {error && (
        <div className="max-w-4xl mx-auto py-8 px-4">
          <p className="text-red-500 text-sm">Error loading lenders: {error.message}</p>
        </div>
      )}
      {!loading && !error && (
        <FinancingCalculatorPage
          lenders={lenders}
          introductionRequests={introductionRequests}
          onRequestIntroduction={handleRequestIntroduction}
          introLoading={introLoading}
          featuredPartners={featuredPartners}
          onSaveScenario={handleSaveScenario}
          defaultPurchasePrice={defaultPurchasePrice}
          financingSnapshots={financingSnapshots}
          onSaveSnapshot={handleSaveSnapshot}
          onLoadSnapshot={handleLoadSnapshot}
          onSetPrimary={handleSetPrimary}
          currentSnapshotId={currentSnapshotId}
          dealTeam={activeDealTeam}
          onShareWithTeam={preselectedSnapshot ? () => setShareModalOpen(true) : undefined}
        />
      )}
      {activeDealTeam && preselectedSnapshot && (
        <ShareSnapshotModal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          onSuccess={() => {
            if (typeof refetch === 'function') refetch();
          }}
          dealTeamId={activeDealTeam.id}
          dealTeamName={activeDealTeam.name}
          preselectedSnapshot={preselectedSnapshot}
        />
      )}
    </AppLayout>
  );
}

const SucceedoFinancingCalculatorContainer = (_props, _railsContext) => (
  <ApolloProvider client={client}>
    <FinancingCalculatorInner />
  </ApolloProvider>
);

export default SucceedoFinancingCalculatorContainer;
